'use client';

/**
 * HKFamilyOfficesChart — self-contained, typed React component (TypeScript).
 *
 * No props. Injects its own scoped styles once, on first mount. Colors match the
 * brand teal palette (see components/charts/family-office.tsx); PS Global logo
 * sits top-right on desktop and is hidden on mobile.
 */
import React, { useState, useEffect, useRef } from 'react';

const NS = 'http://www.w3.org/2000/svg';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Types -------------------------------- */
type View = 'offices' | 'aum';

interface Tier { lbl: string; v: number; }
interface OfficeData {
  y2023: { total: number; tiers: Tier[] };
  y2025: { total: number };
}
interface AumPoint { year: string; v: number; note: string; }
type Attrs = Record<string, string | number>;

/* -------------------------------- Data --------------------------------- */
const OFFICE: OfficeData = {
  y2023: { total: 2703, tiers: [
    { lbl: 'US$10–30M', v: 535 },
    { lbl: 'US$30–50M', v: 601 },
    { lbl: 'US$50–100M', v: 682 },
    { lbl: 'US$100M+',  v: 885 },
  ]},
  y2025: { total: 3384 },
};
// Brand teal ramp, light -> dark (blue-200, blue-400, blue-500, blue-800).
const TIER_FILL: string[] = ['#a3e8e0', '#2db5ad', '#0f9993', '#0d555c'];

const AUM: AumPoint[] = [
  { year: '2021', v: 35.5, note: '' },
  { year: '2022', v: 30.5, note: '−14% · market rout' },
  { year: '2023', v: 31.2, note: '' },
  { year: '2024', v: 35.1, note: 'full recovery · ~US$4.5T' },
];

const INSIGHT: Record<View, React.ReactNode> = {
  offices: <><b>Formation is structural, not cyclical.</b> The sector's first-ever count — taken right after 2022's 14% asset-market decline — already found 2,703 offices, and the 25% growth since has been driven by the FIHV tax regime and succession planning, not market returns.</>,
  aum: <><b>Assets dipped, then fully recovered.</b> Total managed wealth fell 14% in the 2022 rout and only regained its level by end-2024. Office formation, first counted right after that trough, has grown on structural drivers since — the infrastructure lags the wealth.</>,
};

const easeOut = 'cubic-bezier(0.19,1,0.22,1)';

function el<K extends string>(tag: K, attrs: Attrs): SVGElement {
  const e = document.createElementNS(NS, tag) as SVGElement;
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
}

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'hkfo-styles';
const CSS = `
.hkfo-root {
  --hkfo-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --teal: #0f9993; --teal-700: #176a6f; --teal-800: #0d555c; --teal-900: #04445c;
  --teal-50: #ecfbf9; --teal-100: #d0f3ee; --teal-200: #a3e8e0;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --line-2: #EEF1F2; --paper: #FFFFFF; --wash: #F5F8F8;
  box-sizing: border-box; width: 100%;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 34px; color: var(--ink);
  font-family: var(--hkfo-font); -webkit-font-smoothing: antialiased;
}
.hkfo-root *, .hkfo-root *::before, .hkfo-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.hkfo-root h1, .hkfo-root h2, .hkfo-root h3, .hkfo-root p, .hkfo-root img { margin: 0; }
.hkfo-root p { letter-spacing: normal; }
/* Isolate from .blog-content prose styles that out-specify the universal reset: leaked h2 margins, the gold ::after underline, and p margins/letter-spacing. */
.hkfo-root h1, .hkfo-root h2, .hkfo-root h3, .hkfo-root h4, .hkfo-root p { margin: 0; padding: 0; }
.hkfo-root p { letter-spacing: normal; }
.hkfo-root h1::after, .hkfo-root h2::after, .hkfo-root h3::after, .hkfo-root h1::before, .hkfo-root h2::before, .hkfo-root h3::before { content: none !important; display: none !important; }

.hkfo-root .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 40px; flex-wrap: wrap; }
.hkfo-root .head-l { flex: 1; min-width: 0; }
.hkfo-root .head-r { display: flex; flex-direction: column; align-items: flex-end; gap: 18px; flex: none; }
.hkfo-root .hkfo-logo { height: 40px; width: auto; display: block; }
.hkfo-root .eyebrow { font-family: var(--hkfo-font); font-size: 11.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--teal-700); font-weight: 600; display: flex; align-items: center; gap: 8px; }
.hkfo-root .eyebrow .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); }
.hkfo-root .head h2 { font-size: 27px; font-weight: 600; letter-spacing: -0.01em; color: var(--ink); margin-top: 20px; line-height: 1.2; max-width: 560px; }
.hkfo-root .sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 540px; line-height: 1.5; }

.hkfo-root .toggle { display: inline-flex; margin-top: 26px; background: var(--wash); border: 1px solid var(--line); border-radius: 999px; padding: 4px; gap: 2px; flex: none; }
.hkfo-root .toggle button { font-family: var(--hkfo-font); font-size: 13px; font-weight: 500; color: var(--body); background: transparent; border: none; padding: 9px 18px; border-radius: 999px; cursor: pointer; transition: color .2s, background .2s, box-shadow .2s; white-space: nowrap; }
.hkfo-root .toggle button.active { background: var(--paper); color: var(--teal-800); font-weight: 600; box-shadow: 0 2px 8px -2px rgba(4,68,92,0.22); }

.hkfo-root .chart-wrap { margin-top: 30px; position: relative; }
.hkfo-root svg { display: block; width: 100%; height: auto; overflow: visible; }
.hkfo-root .grid line { stroke: var(--line-2); stroke-width: 1; }
.hkfo-root .axis-lbl { font-family: var(--hkfo-font); font-size: 11px; fill: var(--muted); }
.hkfo-root .cat-lbl { font-size: 13px; font-weight: 600; fill: var(--ink-2); }
.hkfo-root .cat-sub { font-family: var(--hkfo-font); font-size: 10.5px; fill: var(--muted); letter-spacing: 0.04em; }
.hkfo-root .val-lbl { font-size: 20px; font-weight: 600; fill: var(--ink); }
.hkfo-root .val-unit { font-size: 12px; font-weight: 500; fill: var(--muted); }
.hkfo-root .bar-seg { cursor: pointer; transition: opacity .18s; }
.hkfo-root .bar-seg:hover { opacity: 0.82; }
.hkfo-root .growth-badge .pill-bg { fill: var(--teal); }
.hkfo-root .growth-badge .pill-tx { fill: #fff; font-size: 13px; font-weight: 600; }
.hkfo-root .growth-badge .pill-sub { fill: rgba(255,255,255,0.85); font-size: 10px; font-weight: 500; font-family: var(--hkfo-font); letter-spacing: 0.03em; }
.hkfo-root .aum-line { fill: none; stroke: var(--teal); stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.hkfo-root .aum-pt { fill: var(--paper); stroke: var(--teal); stroke-width: 3; cursor: pointer; }
.hkfo-root .aum-plabel { font-size: 15px; font-weight: 600; fill: var(--ink); }
.hkfo-root .anno { font-size: 11.5px; font-weight: 500; }
.hkfo-root .anno-strong { font-weight: 600; }

.hkfo-root .tip { position: absolute; pointer-events: none; background: var(--ink); color: #fff; padding: 9px 12px; border-radius: 10px; font-size: 12px; line-height: 1.45; opacity: 0; transform: translate(-50%, -8px); transition: opacity .15s; white-space: nowrap; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5); z-index: 5; }
.hkfo-root .tip b { font-weight: 600; }
.hkfo-root .tip .t-val { color: var(--teal-200); font-family: var(--hkfo-font); }
.hkfo-root .tip::after { content: ''; position: absolute; left: 50%; bottom: -5px; transform: translateX(-50%) rotate(45deg); width: 10px; height: 10px; background: var(--ink); }

.hkfo-root .insight { margin-top: 26px; display: flex; gap: 14px; align-items: flex-start; background: var(--teal-50); border: 1px solid var(--teal-100); border-radius: 14px; padding: 16px 20px; }
.hkfo-root .insight .mark { flex: none; width: 26px; height: 26px; border-radius: 8px; background: var(--teal); display: grid; place-items: center; margin-top: 1px; }
.hkfo-root .insight .mark svg { width: 15px; height: 15px; }
.hkfo-root .insight p { font-size: 14px; color: var(--ink-2); line-height: 1.5; }
.hkfo-root .insight b { color: var(--teal-800); font-weight: 600; }

.hkfo-root .foot { margin-top: 26px; font-size: 11px; color: var(--muted); line-height: 1.6; font-family: var(--hkfo-font); letter-spacing: 0.01em; }
.hkfo-root .foot .hl { color: var(--ink-2); }
.hkfo-root .src-toggle { margin-top: 22px; display: inline-flex; align-items: center; gap: 7px; font-family: var(--hkfo-font); font-size: 11px; letter-spacing: 0.04em; color: var(--ink-2); background: none; border: none; padding: 0; cursor: pointer; }
.hkfo-root .src-toggle:hover { color: var(--ink); }
.hkfo-root .src-toggle svg { width: 11px; height: 11px; }

@media (max-width: 640px) {
  .hkfo-root { padding: 26px 0 24px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
  .hkfo-root .head h2 { font-size: 21px; }
  .hkfo-root .sub { font-size: 14px; line-height: 1.5; }
  .hkfo-root .head { flex-direction: column; }
  .hkfo-root .head-r { width: 100%; align-items: stretch; gap: 0; }
  .hkfo-root .hkfo-logo { display: none; }
  .hkfo-root .toggle { width: 100%; }
  .hkfo-root .toggle button { flex: 1; text-align: center; padding: 10px 8px; }
  .hkfo-root .chart-wrap { margin-top: 10px; }
  .hkfo-root .insight { margin-top: 20px; padding: 14px 16px; }
  .hkfo-root .insight p { font-size: 14px line-height: 1.5; }
}
.hkfo-root svg.mob .axis-lbl { font-size: 15px; }
.hkfo-root svg.mob .cat-lbl { font-size: 18px; }
.hkfo-root svg.mob .cat-sub { font-size: 12.5px; }
.hkfo-root svg.mob .val-lbl { font-size: 27px; }
.hkfo-root svg.mob .aum-plabel { font-size: 20px; }
.hkfo-root svg.mob .anno { font-size: 15px; }
.hkfo-root svg.mob .growth-badge .pill-tx { font-size: 17px; }
.hkfo-root svg.mob .growth-badge .pill-sub { font-size: 11px; }
`;

function useInjectStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

function useInView(ref: React.RefObject<SVGSVGElement | null>): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(() => setInView(true), 0);
      return () => window.clearTimeout(t);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setInView(true); },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, inView]);
  return inView;
}

/* ------------------------------ Component ------------------------------ */
const HKFamilyOfficesChart: React.FC = () => {
  useInjectStyles();
  const [view, setView] = useState<View>('offices');
  const [sourcesOpen, setSourcesOpen] = useState<boolean>(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(svgRef);

  useEffect(() => {
    if (!inView) return;
    const svg = svgRef.current;
    const tip = tipRef.current;
    if (!svg || !tip) return;

    let W: number, H: number, plotW: number, plotH: number, isMobile: boolean;
    let M: { top: number; right: number; bottom: number; left: number };

    function computeLayout(): void {
      isMobile = window.innerWidth <= 640;
      W = isMobile ? 520 : 900;
      H = isMobile ? 470 : 420;
      M = isMobile ? { top: 36, right: 26, bottom: 60, left: 50 }
                   : { top: 46, right: 40, bottom: 54, left: 58 };
      plotW = W - M.left - M.right;
      plotH = H - M.top - M.bottom;
    }
    function clear(): void { while (svg!.firstChild) svg!.removeChild(svg!.firstChild); }
    function showTip(html: string, cx: number, cy: number): void {
      tip!.innerHTML = html;
      const rect = svg!.getBoundingClientRect();
      const scale = rect.width / W;
      tip!.style.left = (cx * scale) + 'px';
      tip!.style.top = (cy * scale) + 'px';
      tip!.style.opacity = '1';
    }
    function hideTip(): void { tip!.style.opacity = '0'; }

    function renderOffices(): void {
      clear();
      const maxV = 3600;
      const yScale = (v: number): number => M.top + plotH - (v / maxV) * plotH;

      const g = el('g', { class: 'grid' });
      [0, 1000, 2000, 3000].forEach((v) => {
        const y = yScale(v);
        g.appendChild(el('line', { x1: M.left, y1: y, x2: W - M.right, y2: y }));
        const t = el('text', { x: M.left - 12, y: y + 4, 'text-anchor': 'end', class: 'axis-lbl' });
        t.textContent = v === 0 ? '0' : (v / 1000) + 'k';
        g.appendChild(t);
      });
      svg!.appendChild(g);

      const barW = isMobile ? 116 : 150;
      const x2023 = M.left + plotW * 0.12;
      const x2025 = M.left + plotW * 0.62;

      let acc = 0;
      const seg2023: { r: SVGElement; yTop: number; h: number }[] = [];
      OFFICE.y2023.tiers.forEach((t, i) => {
        const yTop = yScale(acc + t.v);
        const yBot = yScale(acc);
        const h = yBot - yTop;
        const r = el('rect', { x: x2023, y: yTop, width: barW, height: h, fill: TIER_FILL[i], class: 'bar-seg', rx: 0 });
        r.addEventListener('mouseenter', () => showTip(
          `<b>${t.lbl}</b><br>${t.v.toLocaleString()} offices · <span class="t-val">${Math.round(t.v / OFFICE.y2023.total * 100)}%</span>`,
          x2023 + barW / 2, yTop + h / 2 - 4));
        r.addEventListener('mouseleave', hideTip);
        svg!.appendChild(r);
        seg2023.push({ r, yTop, h });
        acc += t.v;
      });

      const y25top = yScale(OFFICE.y2025.total);
      const bar25 = el('rect', { x: x2025, y: y25top, width: barW, height: yScale(0) - y25top, fill: 'var(--teal-800)', rx: 0, class: 'bar-seg' });
      bar25.addEventListener('mouseenter', () => showTip(
        `<b>End-2025</b><br>${OFFICE.y2025.total.toLocaleString()} offices · <span class="t-val">&gt;½ in US$51M+ tiers</span>`,
        x2025 + barW / 2, y25top + 30));
      bar25.addEventListener('mouseleave', hideTip);
      svg!.appendChild(bar25);

      const mkCat = (x: number, big: string, small: string): void => {
        const t1 = el('text', { x: x + barW / 2, y: H - M.bottom + 24, 'text-anchor': 'middle', class: 'cat-lbl' });
        t1.textContent = big; svg!.appendChild(t1);
        const t2 = el('text', { x: x + barW / 2, y: H - M.bottom + 40, 'text-anchor': 'middle', class: 'cat-sub' });
        t2.textContent = small; svg!.appendChild(t2);
      };
      mkCat(x2023, 'End-2023', 'first-ever count');
      mkCat(x2025, 'End-2025', 'latest estimate');

      const mkVal = (x: number, y: number, num: number): void => {
        const grp = el('g', {});
        grp.style.opacity = '0'; grp.style.transition = 'opacity .45s ease';
        const t = el('text', { x: x + barW / 2, y: y - 14, 'text-anchor': 'middle', class: 'val-lbl' });
        t.textContent = num.toLocaleString(); grp.appendChild(t); svg!.appendChild(grp);
        window.setTimeout(() => { grp.style.opacity = '1'; }, 700);
      };
      mkVal(x2023, seg2023[seg2023.length - 1].yTop, OFFICE.y2023.total);
      mkVal(x2025, y25top, OFFICE.y2025.total);

      const badge = el('g', { class: 'growth-badge' });
      const bx = (x2023 + barW + x2025) / 2;
      const by = (seg2023[0].yTop + y25top) / 2 - 26;
      const arrow = el('path', { d: `M ${x2023 + barW + 8} ${seg2023[0].yTop + 6} C ${bx - 30} ${by - 6}, ${bx + 30} ${by - 6}, ${x2025 - 8} ${y25top + 4}`, fill: 'none', stroke: 'var(--teal)', 'stroke-width': 2, 'stroke-dasharray': '2 5', 'stroke-linecap': 'round' });
      badge.appendChild(arrow);
      const pw = isMobile ? 134 : 118, ph = isMobile ? 46 : 44;
      badge.appendChild(el('rect', { x: bx - pw / 2, y: by - ph / 2, width: pw, height: ph, rx: 12, class: 'pill-bg' }));
      const pt = el('text', { x: bx, y: by - 2, 'text-anchor': 'middle', class: 'pill-tx' });
      pt.textContent = '+681 offices'; badge.appendChild(pt);
      const ps = el('text', { x: bx, y: by + 14, 'text-anchor': 'middle', class: 'pill-sub' });
      ps.textContent = '+25% IN 2 YEARS'; badge.appendChild(ps);
      badge.style.opacity = '0'; badge.style.transition = 'opacity .5s ' + easeOut;
      svg!.appendChild(badge);
      window.setTimeout(() => { badge.style.opacity = '1'; }, 900);

      requestAnimationFrame(() => {
        seg2023.forEach((s, i) => {
          s.r.animate([{ y: yScale(0) + 'px', height: '0px' }, { y: s.yTop + 'px', height: s.h + 'px' }],
            { duration: 900, delay: i * 90, easing: easeOut, fill: 'backwards' });
        });
        bar25.animate([{ y: yScale(0) + 'px', height: '0px' }, { y: y25top + 'px', height: (yScale(0) - y25top) + 'px' }],
          { duration: 950, delay: 260, easing: easeOut, fill: 'backwards' });
      });
    }

    function renderAUM(): void {
      clear();
      const minV = 28, maxV = 37;
      const xScale = (i: number): number => M.left + (i / (AUM.length - 1)) * plotW;
      const yScale = (v: number): number => M.top + plotH - ((v - minV) / (maxV - minV)) * plotH;

      const g = el('g', { class: 'grid' });
      [29, 31, 33, 35, 37].forEach((v) => {
        const y = yScale(v);
        g.appendChild(el('line', { x1: M.left, y1: y, x2: W - M.right, y2: y }));
        const t = el('text', { x: M.left - 12, y: y + 4, 'text-anchor': 'end', class: 'axis-lbl' });
        t.textContent = String(v); g.appendChild(t);
      });
      const unit = el('text', { x: M.left - 46, y: M.top - 18, class: 'axis-lbl' });
      unit.textContent = 'HK$ trillion'; g.appendChild(unit);
      svg!.appendChild(g);

      AUM.forEach((d, i) => {
        const t = el('text', { x: xScale(i), y: H - M.bottom + 26, 'text-anchor': 'middle', class: 'cat-lbl' });
        t.textContent = d.year; svg!.appendChild(t);
      });

      const pts: [number, number][] = AUM.map((d, i) => [xScale(i), yScale(d.v)]);
      let areaD = `M ${pts[0][0]} ${yScale(minV)} `;
      pts.forEach((p) => areaD += `L ${p[0]} ${p[1]} `);
      areaD += `L ${pts[pts.length - 1][0]} ${yScale(minV)} Z`;
      const grad = el('linearGradient', { id: 'hkfoAumGrad', x1: 0, y1: 0, x2: 0, y2: 1 });
      grad.appendChild(el('stop', { offset: '0%', 'stop-color': 'var(--teal)', 'stop-opacity': 0.22 }));
      grad.appendChild(el('stop', { offset: '100%', 'stop-color': 'var(--teal)', 'stop-opacity': 0 }));
      const defs = el('defs', {}); defs.appendChild(grad); svg!.appendChild(defs);
      const area = el('path', { d: areaD, fill: 'url(#hkfoAumGrad)' });
      area.style.opacity = '0'; area.style.transition = 'opacity .7s ease'; svg!.appendChild(area);
      window.setTimeout(() => { area.style.opacity = '1'; }, 500);

      let lineD = `M ${pts[0][0]} ${pts[0][1]} `;
      pts.forEach((p, i) => { if (i > 0) lineD += `L ${p[0]} ${p[1]} `; });
      const line = el('path', { d: lineD, class: 'aum-line' }) as SVGPathElement;
      svg!.appendChild(line);
      const len = line.getTotalLength();
      line.style.strokeDasharray = String(len); line.style.strokeDashoffset = String(len);
      line.style.transition = 'stroke-dashoffset 1.1s ' + easeOut;
      requestAnimationFrame(() => { line.style.strokeDashoffset = '0'; });

      AUM.forEach((d, i) => {
        const [x, y] = pts[i];
        const c = el('circle', { cx: x, cy: y, r: 6, class: 'aum-pt' });
        c.style.opacity = '0'; c.style.transition = 'opacity .3s ease';
        c.addEventListener('mouseenter', () => showTip(
          `<b>End-${d.year}</b><br>HK$${d.v}T managed<span class="t-val">${d.note ? ' · ' + d.note.replace('−', '-') : ''}</span>`,
          x, y - 6));
        c.addEventListener('mouseleave', hideTip);
        svg!.appendChild(c);
        window.setTimeout(() => { c.style.opacity = '1'; }, 600 + i * 220);
        const lbl = el('text', { x: x, y: y - 18, 'text-anchor': 'middle', class: 'aum-plabel' });
        lbl.style.opacity = '0'; lbl.style.transition = 'opacity .3s ease';
        lbl.textContent = d.v.toFixed(1); svg!.appendChild(lbl);
        window.setTimeout(() => { lbl.style.opacity = '1'; }, 700 + i * 220);
      });

      const t2 = pts[1];
      const a1 = el('g', {});
      a1.style.opacity = '0'; a1.style.transition = 'opacity .4s ease';
      a1.appendChild(el('line', { x1: t2[0], y1: t2[1] + 14, x2: t2[0], y2: t2[1] + 44, stroke: '#C6564B', 'stroke-width': 1.5, 'stroke-dasharray': '2 3' }));
      const a1t = el('text', { x: t2[0], y: t2[1] + 60, 'text-anchor': 'middle', class: 'anno anno-strong', fill: '#C6564B' });
      a1t.textContent = '−14% · market rout'; a1.appendChild(a1t); svg!.appendChild(a1);
      window.setTimeout(() => { a1.style.opacity = '1'; }, 1200);

      // Pin this annotation to the top of the plot (above the line) so it
      // doesn't bunch with the 35.1 value label sitting on the 2024 point.
      const t4 = pts[3];
      const a2 = el('g', {});
      a2.style.opacity = '0'; a2.style.transition = 'opacity .4s ease';
      const a2t = el('text', { x: t4[0], y: M.top - 24, 'text-anchor': 'end', class: 'anno anno-strong', fill: 'var(--teal-800)' });
      a2t.textContent = 'full recovery'; a2.appendChild(a2t);
      const a2s = el('text', { x: t4[0], y: M.top - 10, 'text-anchor': 'end', class: 'anno', fill: 'var(--muted)' });
      a2s.textContent = '≈ US$4.5T'; a2.appendChild(a2s); svg!.appendChild(a2);
      window.setTimeout(() => { a2.style.opacity = '1'; }, 1400);
    }

    function draw(): void {
      hideTip();
      computeLayout();
      svg!.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg!.classList.toggle('mob', isMobile);
      if (view === 'offices') renderOffices(); else renderAUM();
    }
    draw();

    let rzT: number | undefined;
    const onResize = (): void => { hideTip(); window.clearTimeout(rzT); rzT = window.setTimeout(draw, 160); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); window.clearTimeout(rzT); clear(); hideTip(); };
  }, [view, inView]);

  return (
    <div className="hkfo-root">
      <div className="head">
        <div className="head-l">
          <div className="eyebrow"><span className="dot"></span> Hong Kong · Single Family Offices</div>
          <h2>A sector counted for the first time — and already up 25%.</h2>
          <p className="sub">Nobody published a Hong Kong single-family-office headcount before end-2023. The first-ever count, taken right after 2022's asset-market decline, already found 2,703 offices — and formation has grown 25% since.</p>
          <div className="toggle" role="tablist">
            <button className={view === 'offices' ? 'active' : ''} role="tab" onClick={() => setView('offices')}>Family offices</button>
            <button className={view === 'aum' ? 'active' : ''} role="tab" onClick={() => setView('aum')}>Assets under mgmt</button>
          </div>
        </div>
        <div className="head-r">
          <img className="hkfo-logo" src={PSGLOBAL_LOGO} alt="PS Global Consulting" />
        </div>
      </div>

      <div className="chart-wrap">
        <svg ref={svgRef} viewBox="0 0 900 420" aria-label="Hong Kong family office growth chart"></svg>
        <div className="tip" ref={tipRef}></div>
      </div>

      <div className="insight">
        <span className="mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M17 7h4v4" /></svg>
        </span>
        <p>{INSIGHT[view]}</p>
      </div>

      {!sourcesOpen ? (
        <button className="src-toggle" onClick={() => setSourcesOpen(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          Show sources
        </button>
      ) : (
        <p className="foot">
          <span className="hl">Sources:</span> Deloitte / InvestHK SFO estimates (end-2023 &amp; end-2025, same 200K-UHNW methodology) · SFC Asset &amp; Wealth Management Activities Survey (year-end AUM) · FSTB / HKSAR Government. Figures are modeled statistical estimates built on a UHNW database, not a registry count — Hong Kong has no SFO registration requirement. 2023 tiers shown as measured; the end-2025 study reports over half of the 3,384 offices in wealth tiers of US$51M and above, on revised tier boundaries. AUM is the closest official proxy for managed private wealth.
        </p>
      )}
    </div>
  );
};

export default HKFamilyOfficesChart;
